import React, { useEffect, useRef, useState } from "react"

import { FormProvider } from "react-hook-form"
import { useSearchParams } from "react-router-dom"

import { PoliciesListType } from "@common-shared/config/oneVerify/oneVerifyPolicies.config.ts"

import { NIRVANA_THEME } from "@front-shared/config/theme.config.ts"
import { FRONT_VALIDATION_CONFIG } from "@front-shared/config/validation.config.ts"
import { ButtonElement } from "@front-shared/elements/button.element.tsx"
import { DatePickerElement } from "@front-shared/elements/datePicker.element.tsx"
import { PageHelmetElement } from "@front-shared/elements/pageHelmet.element.tsx"
import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"
import { SpinnerElement } from "@front-shared/elements/spinner.element.tsx"
import { useIsMobile } from "@front-shared/hooks/isMobile.hook"

import { CHECKER_CONFIG } from "../../../config/checker.config"
import { GENERAL_CONFIG } from "../../../config/general.config"
import { UtilHelper } from "../../../helpers/util.helper"
import { useSingleOverride } from "../../../hooks/singleOverride.hooks"
import { useSinglePolicyView } from "../../../hooks/singlePolicyView.hooks.ts"
import { useSinglePolicyViewNavigation } from "../../../hooks/singlePolicyViewNavigation.hooks.ts"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { coverageOverridesSelector } from "../../../store/selectors/coverageOverrides.selector"
import { policiesSelector } from "../../../store/selectors/policies.selector"
import { CoverageResultBreakdownWrapperComponent } from "../../coverageChecker/result/coverageResultBreakdownWrapper.component"
import { CoverageResultGeneralComponent } from "../../coverageChecker/result/coverageResultGeneral.component"
import { AgenticCheckStatusComponent } from "../agentic/agenticCheckStatus.component"
import { CoverageResultFlagsComponent } from "../flags/coverageResultFlags.component"
import { PolicySidebarComponent } from "../sidebar/policySidebar.component.tsx"
import { WriteToEhrButtonComponent } from "../writeToEhr/writeToEhrButton.component"
import { FindSimilarButtonPolicyButton } from "./header/findSImilarPolicyButton.component.tsx"
import { SinglePolicyRetryButtonComponent } from "./header/singlePolicyRetryButton.component"
import { TaskAssignmentStatusChange } from "./header/taskAssignmentStatusChange.component.tsx"

import arrowLeftIcon from "@front-shared/assets/images/icons/arrow-left-dark.svg"
import editIcon from "@front-shared/assets/images/icons/edit-dark.svg"

export const SinglePolicyViewComponent: React.FunctionComponent<
    ISinglePolicyViewComponentProps
> = props => {
    const isTablet = useIsMobile(NIRVANA_THEME.mediaQueries.md)

    const [searchParams] = useSearchParams()

    const overridesState = useAppSelector(coverageOverridesSelector)
    const { selectedItem } = useAppSelector(policiesSelector)

    const headerRef = useRef<HTMLDivElement | null>(null)
    const [headerHeight, setHeaderHeight] = useState<number>(0)

    // Measure header height and update on selected item change (because of possible different content in header which can change its height)
    useEffect(() => {
        const headerElement = headerRef.current

        if (!headerElement) {
            return
        }

        const updateHeaderHeight = () => {
            setHeaderHeight(headerElement.getBoundingClientRect().height)
        }

        updateHeaderHeight()

        if (typeof ResizeObserver === "undefined") {
            return
        }

        const observer = new ResizeObserver(() => {
            updateHeaderHeight()
        })

        observer.observe(headerElement)

        return () => {
            observer.disconnect()
        }
    }, [selectedItem])

    const {
        setTasksListTypeNeedsReload,

        onGoBack,
        navigateToPolicyByRequestId,
        onSmartScanSuccessRedirect,
    } = useSinglePolicyViewNavigation(props.listType)

    const {
        isRetryingCoverageCheckLoading,
        commonCoverageCheckResult,
        isLatestCheck,
        currentPolicyIdentificationData,
        currentPatientIdentificationData,

        reloadPoliciesList,
        getSinglePolicy,
    } = useSinglePolicyView(props.listType, props.nirvanaRequestId)

    const {
        overridesFormMethods,
        isOverridesEnabled,
        isOverrideModeActive,
        setIsOverrideModeActive,
        overridesFormKey,
        canOverridePolicyCoverage,
        isDeleteOverrideLoading,
        isCreateOverrideLoading,
        isOverrideActionsDisabled,
        onDeleteOverride,
        onResetOverride,
        onSaveOverride,
        onUseSuggestedOverrideExpiration,
        isFormSubmitDisabled,
        suggestedOverrideExpirationDate,
        minOverrideExpirationDate,
        dataWatcher: overridesDataWatcher,
    } = useSingleOverride(
        selectedItem,
        overridesState.selectedPolicyOverride,
        props.nirvanaRequestId,
        getSinglePolicy,
        reloadPoliciesList,
        navigateToPolicyByRequestId,
    )

    return (
        <div className="relative">
            <PageHelmetElement
                title="Policy"
                defaultPageTitle={GENERAL_CONFIG.defaultPageTitle}
            />

            {selectedItem ? (
                <>
                    <FormProvider {...overridesFormMethods}>
                        <div
                            ref={headerRef}
                            className="
                                flex items-center px-[16px] py-[16px]
                                border-b border-solid border-brand-warmLight
                                md:block
                            "
                        >
                            <div className="flex-1 mr-[16px] flex items-start">
                                <div
                                    className="px-[8px] cursor-pointer relative top-[8px] mr-[8px]"
                                    onClick={onGoBack}
                                >
                                    <img src={arrowLeftIcon} alt="Back" />
                                </div>

                                <div className="flex-1">
                                    <PrimaryText
                                        typography="h3"
                                        className="mouseflow-ignore"
                                    >
                                        {UtilHelper.capitalizeWordsInSentence(
                                            [
                                                selectedItem.coverageResult
                                                    .demographics?.firstName ||
                                                    selectedItem.requestData
                                                        .firstName ||
                                                    "John",
                                                selectedItem.coverageResult
                                                    .demographics?.lastName ||
                                                    selectedItem.requestData
                                                        .lastName ||
                                                    "Doe",
                                            ].join(" "),
                                        )}
                                    </PrimaryText>

                                    <CoverageResultFlagsComponent
                                        flags={
                                            selectedItem.coverageResult.flags
                                        }
                                    />
                                </div>
                            </div>

                            {isLatestCheck ? (
                                <div
                                    className="
                                        flex items-start
                                        md:mt-[24px]
                                        sm:block
                                    "
                                >
                                    {!isOverrideModeActive ? (
                                        <SinglePolicyRetryButtonComponent
                                            className="mr-[16px] sm:mr-[0px]"
                                            listType={props.listType}
                                            navigateToPolicyByRequestId={
                                                navigateToPolicyByRequestId
                                            }
                                        />
                                    ) : null}

                                    {canOverridePolicyCoverage ? (
                                        <>
                                            {currentPolicyIdentificationData &&
                                            !isOverrideModeActive ? (
                                                <ButtonElement
                                                    className="sm:mt-[8px]"
                                                    buttonClassName="sm:w-full"
                                                    htmlType="button"
                                                    type="default"
                                                    label={
                                                        overridesState.selectedPolicyOverride
                                                            ? "Edit Override"
                                                            : "Override"
                                                    }
                                                    icon={editIcon}
                                                    isRightIcon={true}
                                                    disabled={
                                                        isOverrideActionsDisabled ||
                                                        isRetryingCoverageCheckLoading
                                                    }
                                                    onClick={() => {
                                                        setIsOverrideModeActive(
                                                            true,
                                                        )
                                                    }}
                                                />
                                            ) : currentPolicyIdentificationData &&
                                              isOverrideModeActive ? (
                                                <div
                                                    className="flex items-center sm:block"
                                                    key={overridesFormKey}
                                                >
                                                    <PrimaryText
                                                        typography="textSmall"
                                                        className="mr-[8px] min-w-0 flex-shrink-0"
                                                    >
                                                        Overrides Expiration
                                                        Date
                                                    </PrimaryText>

                                                    <div className="relative mr-[16px] md:max-w-[200px] sm:mr-[0px]">
                                                        <DatePickerElement
                                                            name="expirationDate"
                                                            size="small"
                                                            defaultValue={
                                                                overridesDataWatcher.expirationDate ||
                                                                undefined
                                                            }
                                                            reactHookFormValidations={{
                                                                required:
                                                                    FRONT_VALIDATION_CONFIG.required,
                                                            }}
                                                            shouldValidate
                                                            reactHookFormErrors={
                                                                overridesFormMethods
                                                                    .formState
                                                                    .errors
                                                            }
                                                            reactHookFormRegister={
                                                                overridesFormMethods.register
                                                            }
                                                            reactHookFormUnregister={
                                                                overridesFormMethods.unregister
                                                            }
                                                            reactHookFormSet={
                                                                overridesFormMethods.setValue
                                                            }
                                                            minDate={minOverrideExpirationDate.toDate()}
                                                            disabled={
                                                                isCreateOverrideLoading
                                                            }
                                                        />

                                                        {!overridesDataWatcher.expirationDate ? (
                                                            <PrimaryText
                                                                typography="caption"
                                                                className="absolute bottom-[-20px] whitespace-nowrap"
                                                            >
                                                                Suggested:{" "}
                                                                <span
                                                                    className="cursor-pointer text-brand-vibrantPurple underline text-bold"
                                                                    onClick={
                                                                        onUseSuggestedOverrideExpiration
                                                                    }
                                                                >
                                                                    {suggestedOverrideExpirationDate.format(
                                                                        GENERAL_CONFIG.defaultMomentDateFormat,
                                                                    )}
                                                                </span>
                                                            </PrimaryText>
                                                        ) : null}
                                                    </div>

                                                    <div className="flex items-center sm:mt-[40px] sm:block">
                                                        <ButtonElement
                                                            buttonClassName="w-[100px] sm:w-full"
                                                            htmlType="button"
                                                            type="default"
                                                            label="Reset"
                                                            disabled={
                                                                isCreateOverrideLoading
                                                            }
                                                            onClick={() => {
                                                                onResetOverride()
                                                            }}
                                                        />

                                                        <ButtonElement
                                                            className="ml-[16px] sm:ml-[0px] sm:mt-[8px]"
                                                            buttonClassName="w-[100px] sm:w-full"
                                                            htmlType="button"
                                                            type="default"
                                                            isDanger
                                                            label="Cancel"
                                                            disabled={
                                                                isCreateOverrideLoading
                                                            }
                                                            onClick={() => {
                                                                setIsOverrideModeActive(
                                                                    false,
                                                                )
                                                            }}
                                                        />

                                                        <ButtonElement
                                                            className="ml-[16px] sm:ml-[0px] sm:mt-[8px]"
                                                            buttonClassName="w-[100px] sm:w-full"
                                                            htmlType="button"
                                                            type="primary"
                                                            label="Save"
                                                            disabled={
                                                                isFormSubmitDisabled
                                                            }
                                                            isLoading={
                                                                isCreateOverrideLoading
                                                            }
                                                            onClick={overridesFormMethods.handleSubmit(
                                                                onSaveOverride,
                                                            )}
                                                        />
                                                    </div>
                                                </div>
                                            ) : null}

                                            {overridesState.selectedPolicyOverride &&
                                            !isOverrideModeActive ? (
                                                <ButtonElement
                                                    className="ml-[16px] sm:mt-[8px] sm:ml-[0px]"
                                                    buttonClassName="sm:w-full"
                                                    htmlType="button"
                                                    type="default"
                                                    isDanger
                                                    label="Remove Override"
                                                    isLoading={
                                                        isDeleteOverrideLoading
                                                    }
                                                    disabled={
                                                        isOverrideActionsDisabled ||
                                                        isRetryingCoverageCheckLoading
                                                    }
                                                    onClick={onDeleteOverride}
                                                />
                                            ) : null}
                                        </>
                                    ) : null}

                                    {!isOverrideModeActive ? (
                                        <>
                                            <FindSimilarButtonPolicyButton
                                                className={
                                                    props.listType !==
                                                    PoliciesListType.AGENTIC
                                                        ? "ml-[16px] sm:ml-[0px] sm:mt-[8px]"
                                                        : "hidden"
                                                }
                                                disabled={
                                                    isRetryingCoverageCheckLoading
                                                }
                                                listType={props.listType}
                                            />

                                            <TaskAssignmentStatusChange
                                                className="ml-[16px] sm:ml-[0px] sm:mt-[8px]"
                                                listType={props.listType}
                                                setTasksListTypeNeedsReload={
                                                    setTasksListTypeNeedsReload
                                                }
                                            />

                                            <AgenticCheckStatusComponent
                                                className="ml-[16px] sm:ml-[0px] sm:mt-[8px]"
                                                agenticCheck={
                                                    selectedItem.agenticCheck
                                                }
                                            />

                                            <WriteToEhrButtonComponent
                                                className="ml-[16px] sm:ml-[0px] sm:mt-[8px]"
                                                listType={props.listType}
                                                coverageCheckResult={
                                                    commonCoverageCheckResult
                                                }
                                            />
                                        </>
                                    ) : null}
                                </div>
                            ) : (
                                <div
                                    className="
                                        rounded-[8px] border border-solid border-brand-warmLight
                                        bg-brand-offWhite p-[12px] mr-[16px] md:mr-[0px] md:mt-[16px]
                                    "
                                >
                                    <PrimaryText typography="textSemibold">
                                        Historical Check
                                    </PrimaryText>

                                    <PrimaryText
                                        typography="textSmall"
                                        className="opacity-80"
                                    >
                                        Please go to the most recent check to
                                        perform actions on this policy
                                    </PrimaryText>
                                </div>
                            )}
                        </div>

                        <div
                            className="flex items-start md:block"
                            style={{
                                height: isTablet
                                    ? undefined
                                    : `calc(100vh - ${
                                          GENERAL_CONFIG.headerHeight +
                                          headerHeight
                                      }px)`,
                            }}
                        >
                            <div className="flex-1 h-full overflow-y-auto self-stretch pb-[48px]">
                                <CoverageResultGeneralComponent
                                    isSmartScanResult={
                                        CHECKER_CONFIG.activeCoverageResults.includes(
                                            selectedItem.coverageResult
                                                .resultType,
                                        ) &&
                                        (selectedItem.coverageResult
                                            .isSmartScanResult ||
                                            !!searchParams.get(
                                                GENERAL_CONFIG
                                                    .urlSearchParamsKeys
                                                    .isSmartScanResult,
                                            ))
                                    }
                                    hideAgenticCheckStatus={true} // We don't need to show the AgenticCheckStatusComponent here because we're showing it above
                                    coverageCheckHistory={selectedItem}
                                />

                                <CoverageResultBreakdownWrapperComponent
                                    coverageResult={selectedItem.coverageResult}
                                    coverageOverride={
                                        // Only pass override to breakdown component if overrides are enabled and we're on the latest check
                                        // Other checks will use override that comes from history
                                        // Because we don't need strict state for historical checks
                                        isOverridesEnabled && isLatestCheck
                                            ? overridesState.selectedPolicyOverride ||
                                              undefined
                                            : undefined
                                    }
                                    requestData={selectedItem.requestData}
                                    isSmartScanFloating={true}
                                    smartScanRightMarginClassName="mr-[300px]"
                                    onSmartScanSuccess={
                                        onSmartScanSuccessRedirect
                                    }
                                    requestPayer={selectedItem.requestPayer}
                                    responsePayer={selectedItem.responsePayer}
                                    policy={selectedItem.policy}
                                    agenticCheck={selectedItem.agenticCheck}
                                    preAgenticCoverageResult={
                                        selectedItem.preAgenticCoverageResult
                                    }
                                    requestedHealthProvider={
                                        selectedItem.requestedHealthProvider
                                    }
                                    ignoreReporting={true}
                                    isOverridesModeActivated={
                                        isOverrideModeActive
                                    }
                                    overridesFormKey={overridesFormKey}
                                    isOverridesFormDisabled={
                                        isCreateOverrideLoading
                                    }
                                />
                            </div>

                            <PolicySidebarComponent
                                className={`self-stretch ${isOverrideModeActive ? "hidden" : ""}`}
                                policy={currentPolicyIdentificationData}
                                patient={
                                    currentPatientIdentificationData ||
                                    undefined
                                }
                                onCommentAdded={() => {
                                    setTasksListTypeNeedsReload(true)
                                }}
                            />
                        </div>
                    </FormProvider>
                </>
            ) : (
                <div className="text-center mt-[32px]">
                    <SpinnerElement />
                </div>
            )}
        </div>
    )
}

interface ISinglePolicyViewComponentProps {
    className?: string
    listType: PoliciesListType
    nirvanaRequestId: string
}
